import { useTranslate } from '@tolgee/react'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { MRT_Column, MRT_RowData, MRT_TableInstance, MRT_TableOptions } from 'material-react-table'
import { TomTableActionsProps } from '../theme/components/table/molecules/TomTableActions'
import { TomTableToolbarAlertBanner } from '../theme/components/table/organisms/TomTableToolbarAlertBanner'
import {
  overwriteMinimalTableBodyCellStyle,
  overwriteMinimalTableBodyRowStyle,
  overwriteMinimalTableDetailPanelStyle,
  overwriteMinimalTableFilterTextFieldProps,
  overwriteMinimalTableHeadCellStyle,
  overwriteMinimalTableMuiSelectCheckboxPropsStyle,
  overwriteMinimalTableTableBodyStyle,
  overwriteMinimalTableToolbarAlertBannerStyle,
} from '../theme/overrides/MaterialReactTable'
import { TomTableDataExportProps } from './use-tom-table'

type DateRangeFilterValue = {
  start: dayjs.Dayjs | null
  end: dayjs.Dayjs | null
}
export const useDefaultMRTOptions = <TData extends MRT_RowData>(
  tableActions?: TomTableActionsProps<TData>[],
  dataExport?: TomTableDataExportProps<TData>,
): Partial<MRT_TableOptions<TData>> => {
  const { t } = useTranslate()

  dayjs.extend(isSameOrBefore)
  dayjs.extend(isSameOrAfter)

  const getIsLastPinnedLeft = ({
    column,
    table,
  }: {
    column: MRT_Column<TData>
    table: MRT_TableInstance<TData>
  }) =>
    table
      .getAllColumns()
      .filter(col => col.getIsPinned() === 'left')
      .slice(-1)[0]?.id === column.id

  const getIsControlColumn = (column: MRT_Column<TData>) => {
    const controlColumnIds = ['mrt-row-expand', 'mrt-row-select', 'mrt-row-actions']
    return controlColumnIds.includes(column.id)
  }

  return {
    muiToolbarAlertBannerProps: overwriteMinimalTableToolbarAlertBannerStyle,
    muiDetailPanelProps: overwriteMinimalTableDetailPanelStyle,
    muiSelectCheckboxProps: overwriteMinimalTableMuiSelectCheckboxPropsStyle,
    muiFilterTextFieldProps: overwriteMinimalTableFilterTextFieldProps,
    muiTableBodyProps: overwriteMinimalTableTableBodyStyle,
    muiTableBodyCellProps: ({ column, table }) => {
      const isLastPinnedLeft = getIsLastPinnedLeft({ column, table })
      const isControlColumn = getIsControlColumn(column)

      return overwriteMinimalTableBodyCellStyle(isLastPinnedLeft, isControlColumn)
    },
    muiTableHeadCellProps: ({ column, table }) => {
      const isLastPinnedLeft = getIsLastPinnedLeft({ column, table })
      const isControlColumn = getIsControlColumn(column)

      return overwriteMinimalTableHeadCellStyle(isLastPinnedLeft, isControlColumn)
    },
    muiTableBodyRowProps: ({ row }) =>
      overwriteMinimalTableBodyRowStyle(row.getIsExpanded(), row.getIsSelected()),

    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: true,
    enableColumnOrdering: true,
    enableToolbarInternalActions: true,

    enableGlobalFilter: false,
    enableTopToolbar: true,
    positionToolbarAlertBanner: 'head-overlay',
    renderToolbarAlertBannerContent: ({ table }) => (
      <TomTableToolbarAlertBanner
        table={table}
        tableActions={tableActions}
        dataExport={dataExport}
      />
    ),

    enableColumnActions: false,
    positionActionsColumn: 'last',

    enableColumnFilters: true,
    enableColumnFilterModes: true,
    columnFilterDisplayMode: 'subheader',

    enableSelectAll: true,
    selectAllMode: 'all',

    enableStickyHeader: true,

    enableRowSelection: true,
    enableBatchRowSelection: true,
    enableMultiRowSelection: true,
    enableExpanding: true,
    enableExpandAll: true,
    enableRowActions: true,

    displayColumnDefOptions: {
      'mrt-row-expand': {
        grow: false,
        size: 36,
        minSize: 36,
        maxSize: 36,
      },
      'mrt-row-select': {
        grow: false,
        size: 42,
        minSize: 42,
        maxSize: 42,
        header: '',
      },
      'mrt-row-actions': {
        grow: false,
        minSize: 42,
        maxSize: 42,
        size: 42,
        header: '',
      },
    },

    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 260px)',
        minHeight: 'calc(100vh - 260px)',
      },
    },

    initialState: {
      density: 'compact',
      showColumnFilters: true,
      columnPinning: {
        left: ['mrt-row-actions', 'mrt-row-expand', 'mrt-row-select', 'number'],
      },
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },

    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50, 100],
      showFirstButton: false,
      showLastButton: false,
    },

    filterFns: {
      customDateEqualFn: (row, columnId, filterValue?: DateRangeFilterValue) => {
        if (!filterValue || !filterValue.start) return true

        const date = row.getValue<Date>(columnId)
        const dayJsDate = dayjs(date)

        return dayJsDate.isSame(filterValue.start, 'day')
      },
      customDateBetweenFn: (row, columnId, filterValue?: DateRangeFilterValue) => {
        if (!filterValue) return true

        const date = row.getValue<Date>(columnId)
        const dayJsDate = dayjs(date)

        const startFilterDate = dayjs(filterValue.start)
        const endFilterDate = dayjs(filterValue.end)

        if (filterValue.start && filterValue.end) {
          return (
            dayJsDate.isSameOrAfter(startFilterDate, 'day') &&
            dayJsDate.isSameOrBefore(endFilterDate, 'day')
          )
        } else if (filterValue.start) {
          return dayJsDate.isSameOrAfter(startFilterDate, 'day')
        } else if (filterValue.end) {
          return dayJsDate.isSameOrBefore(endFilterDate, 'day')
        }

        return true
      },
      customDateBeforeFn: (row, columnId, filterValue?: DateRangeFilterValue) => {
        if (!filterValue || !filterValue.start) return true

        const date = row.getValue<Date>(columnId)
        const dayJsDate = dayjs(date)

        return dayJsDate.isSameOrBefore(filterValue.start, 'day')
      },
      customDateAfterFn: (row, columnId, filterValue?: DateRangeFilterValue) => {
        if (!filterValue || !filterValue.start) return true

        const date = row.getValue<Date>(columnId)
        const dayJsDate = dayjs(date)

        return dayJsDate.isSameOrAfter(filterValue.start, 'day')
      },
    },

    localization: {
      filterCustomDateBetweenFn: t('customDateBetweenFnLabel', 'Between'),
      filterCustomDateEqualFn: t('customDateEqualFnLabel', 'Equals'),
      filterCustomDateAfterFn: t('customDateAfterFnLabel', 'From'),
      filterCustomDateBeforeFn: t('customDateBeforeFnLabel', 'Before'),
      noRecordsToDisplay: t('noResultFound', 'No result found'),
      rowsPerPage: t('rowsPerPage', 'Rows per page'),
      of: t('of', 'of'),
    } as any, // Convert to any to avoid typing issue with localization of custom filter function names
  }
}
